import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import type { StoreListItem } from '../../types/admin';
import { AdminPageHeader, copyToClipboard } from './adminShared';
import './AdminLayout.css';

const AdminUserNew: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreListItem[]>([]);
  const [storeId, setStoreId] = useState(searchParams.get('storeId') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStores, setLoadingStores] = useState(true);
  const [error, setError] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [createdEmail, setCreatedEmail] = useState('');

  useEffect(() => {
    adminApi
      .listStores({ active: true })
      .then((data) => setStores(data.items))
      .catch(() => setError('No se pudieron cargar las tiendas'))
      .finally(() => setLoadingStores(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeId || !email.trim()) {
      setError('Tienda y email son obligatorios');
      return;
    }

    setLoading(true);
    setError('');
    setGeneratedPassword('');
    setCreatedEmail('');

    try {
      const result = await adminApi.createUser({
        email: email.trim(),
        storeId,
        password: password.trim() || undefined,
      });

      if (result.password) {
        setGeneratedPassword(result.password);
        setCreatedEmail(result.email);
      } else {
        navigate(storeId ? `/admin/stores/${storeId}` : '/admin/users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creando usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (generatedPassword) {
      await copyToClipboard(generatedPassword);
    }
  };

  return (
    <>
      <AdminPageHeader title="Nuevo usuario" />

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {generatedPassword ? (
        <div className="admin-card">
          <div className="admin-alert admin-alert-success">
            Usuario <strong>{createdEmail}</strong> creado correctamente.
          </div>
          <p>Contraseña generada (guardala, no se vuelve a mostrar):</p>
          <div className="admin-password-box">{generatedPassword}</div>
          <div className="admin-form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="admin-btn admin-btn-primary" onClick={handleCopy}>
              Copiar contraseña
            </button>
            <Link
              to={storeId ? `/admin/stores/${storeId}` : '/admin/users'}
              className="admin-btn admin-btn-secondary"
              style={{ textDecoration: 'none' }}
            >
              Continuar
            </Link>
          </div>
        </div>
      ) : (
        <div className="admin-card">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="admin-form-row">
              <label htmlFor="storeId">Tienda</label>
              <select
                id="storeId"
                className="admin-select"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                disabled={loadingStores}
                required
              >
                <option value="">Seleccionar tienda…</option>
                {stores.map((store) => (
                  <option key={store.store_id} value={store.store_id}>
                    {store.store_name} ({store.store_id})
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-form-row">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="admin-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="admin-form-row">
              <label htmlFor="password">Contraseña (opcional)</label>
              <input
                id="password"
                type="text"
                className="admin-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Se genera automáticamente si está vacío"
              />
            </div>

            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary" disabled={loading}>
                {loading ? 'Creando…' : 'Crear usuario'}
              </button>
              <Link to="/admin/users" className="admin-btn admin-btn-secondary" style={{ textDecoration: 'none' }}>
                Cancelar
              </Link>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default AdminUserNew;
